import React, { useState, useMemo } from 'react';
import { Box, Typography, Button, Stack, Dialog, DialogTitle, DialogContent, DialogActions, Alert } from '@mui/material';
import { Download as DownloadIcon, LinkedIn, Facebook, Info } from '@mui/icons-material';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

interface CourseCertificateProps {
  studentName: string;
  courseName: string;
  completionDate: string;
  position?: string;
  companyName?: string;
  companyLogo?: string;
  certificateSigner?: {
    firstName?: string;
    lastName?: string;
    position?: string;
    signature?: string;
  };
  showCTOSignature?: boolean;
  onDownload?: () => void;
  hideShareButtons?: boolean;
  allowViewCertificates?: boolean;
}

const CourseCertificate: React.FC<CourseCertificateProps> = ({
  studentName,
  courseName,
  completionDate,
  position = "Completado",
  companyName,
  companyLogo,
  certificateSigner,
  showCTOSignature = true,
  onDownload,
  hideShareButtons = false,
  allowViewCertificates = true
}) => {
  const [showInstructions, setShowInstructions] = useState(false);
  
  // Construir URL completa del logo si es una ruta relativa
  const logoUrl = useMemo(() => {
    if (!companyLogo) return null;
    
    // URL absoluta, blob (vista previa sin guardar) o data URI
    if (
      companyLogo.startsWith('http://') ||
      companyLogo.startsWith('https://') ||
      companyLogo.startsWith('blob:') ||
      companyLogo.startsWith('data:')
    ) {
      return companyLogo;
    }
    
    // Si es una ruta relativa, construir la URL completa
    // En desarrollo siempre usar localhost:5000, en producción usar el mismo origen
    let apiBaseUrl: string;
    if (process.env.NODE_ENV === 'production') {
      apiBaseUrl = window.location.origin;
    } else {
      // En desarrollo, siempre usar el backend local
      apiBaseUrl = 'http://localhost:5000';
    }
    
    // Asegurar que la ruta relativa comience con /
    const logoPath = companyLogo.startsWith('/') ? companyLogo : `/${companyLogo}`;
    
    return `${apiBaseUrl}${logoPath}`;
  }, [companyLogo]);

  // Construir URL completa de la firma si es una ruta relativa
  const signatureUrl = useMemo(() => {
    if (!certificateSigner?.signature) return null;
    
    if (
      certificateSigner.signature.startsWith('http://') ||
      certificateSigner.signature.startsWith('https://') ||
      certificateSigner.signature.startsWith('blob:') ||
      certificateSigner.signature.startsWith('data:')
    ) {
      return certificateSigner.signature;
    }
    
    // Si es una ruta relativa, construir la URL completa
    // En desarrollo siempre usar localhost:5000, en producción usar el mismo origen
    let apiBaseUrl: string;
    if (process.env.NODE_ENV === 'production') {
      apiBaseUrl = window.location.origin;
    } else {
      // En desarrollo, siempre usar el backend local
      apiBaseUrl = 'http://localhost:5000';
    }
    
    // Asegurar que la ruta relativa comience con /
    const signaturePath = certificateSigner.signature.startsWith('/') 
      ? certificateSigner.signature 
      : `/${certificateSigner.signature}`;
    
    return `${apiBaseUrl}${signaturePath}`;
  }, [certificateSigner?.signature]);
  const handleDownload = async () => {
    // Validar si la empresa permite ver certificados
    if (!allowViewCertificates) {
      alert('Su empresa no permite la descarga de certificados. Por favor, contacte a su administrador.');
      return;
    }

    const certificateElement = document.getElementById('certificate');
    if (!certificateElement) return;

    try {
      const canvas = await html2canvas(certificateElement, {
        scale: 3,
        useCORS: true,
        allowTaint: false,
        backgroundColor: '#ffffff',
        logging: false,
        imageTimeout: 15000
      });

      const imgData = canvas.toDataURL('image/png', 1.0);
      
      // Calcular dimensiones del PDF basadas en el contenido
      const imgWidth = 297; // A4 landscape width in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      // Crear PDF con altura personalizada si es necesario (más alto que A4)
      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: imgHeight > 210 ? [277, imgHeight] : 'a4' // Si la altura es mayor que A4, usar altura personalizada
      });
      
      pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
      pdf.save(`certificado-${studentName.replace(/\s+/g, '-')}-${courseName.replace(/\s+/g, '-')}.pdf`);
      
      if (onDownload) {
        onDownload();
      }
    } catch (error) {
      console.error('Error generando certificado:', error);
    }
  };

  const handleShareLinkedIn = async () => {
    try {
      // Generar imagen del certificado para descarga
      const certificateElement = document.getElementById('certificate');
      if (!certificateElement) return;

      const canvas = await html2canvas(certificateElement, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff'
      });

      // Crear enlace de descarga para la imagen
      const link = document.createElement('a');
      link.download = `certificado-${studentName.replace(/\s+/g, '-')}-${courseName.replace(/\s+/g, '-')}.png`;
      link.href = canvas.toDataURL('image/png');
      
      // Descargar la imagen automáticamente
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Crear el texto del post mejorado
      const postText = `🚀 Si quieres impulsar tu carrera y adaptarte a los nuevos desafíos del mundo empresarial, la ciberseguridad es el camino.\n\nAcabo de completar el curso "${courseName}" y fue clave para entender cómo proteger la información y aportar más valor a mi organización. Este logro representa mi compromiso con el aprendizaje continuo y el desarrollo profesional. Estoy emocionado de aplicar estos nuevos conocimientos en mi carrera. 🎓 ¡Certificado obtenido! 🎓\n\n💡 Te invito a vivir esta experiencia: https://learning.vigilantium.com/\n\n#Ciberseguridad #CarreraProfesional #SeguridadDigital #AprendizajeContinuo #Vigilantium`;

      // URL de LinkedIn para compartir con texto prellenado
      // Usamos el formato que permite agregar imagen después
      const linkedInUrl = `https://www.linkedin.com/feed/?shareActive=true&text=${encodeURIComponent(postText)}`;

      // Mostrar instrucciones
      setShowInstructions(true);

      // Abrir LinkedIn en una nueva ventana
      window.open(linkedInUrl, '_blank', 'width=600,height=600');

    } catch (error) {
      console.error('Error compartiendo en LinkedIn:', error);
      // Fallback: abrir LinkedIn sin texto prellenado
      const linkedInUrl = `https://www.linkedin.com/feed/?shareActive=true`;
      window.open(linkedInUrl, '_blank', 'width=600,height=600');
    }
  };

  const handleShareFacebook = async () => {
    try {
      // Generar imagen del certificado para descarga
      const certificateElement = document.getElementById('certificate');
      if (!certificateElement) return;

      const canvas = await html2canvas(certificateElement, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff'
      });

      // Crear enlace de descarga para la imagen
      const link = document.createElement('a');
      link.download = `certificado-${studentName.replace(/\s+/g, '-')}-${courseName.replace(/\s+/g, '-')}.png`;
      link.href = canvas.toDataURL('image/png');
      
      // Descargar la imagen automáticamente
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Crear el texto del post para Facebook
      const postText = `🎓 ¡Certificado obtenido! 🎓\n\nAcabo de completar exitosamente el curso "${courseName}".\n\nEste logro representa mi compromiso con el aprendizaje continuo y el desarrollo profesional. Estoy emocionado de aplicar estos nuevos conocimientos en mi carrera.\n\n💡 ¿Has completado algún curso recientemente? ¡Comparte tu experiencia en los comentarios!\n\n#Aprendizaje #DesarrolloProfesional #Certificación #EducaciónContinua #${courseName.replace(/\s+/g, '')} #CrecimientoProfesional`;

      // URL de Facebook para compartir
      const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}&quote=${encodeURIComponent(postText)}`;

      // Abrir Facebook en una nueva ventana
      window.open(facebookUrl, '_blank', 'width=600,height=400');

    } catch (error) {
      console.error('Error compartiendo en Facebook:', error);
      // Fallback: abrir Facebook con texto simple
      const postText = `🎓 ¡Acabo de completar el curso "${courseName}"! 🎓\n\n#Aprendizaje #DesarrolloProfesional #Certificación`;
      const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}&quote=${encodeURIComponent(postText)}`;
      window.open(facebookUrl, '_blank', 'width=600,height=400');
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
      {/* Certificado */}
      <Box
        id="certificate"
        sx={{
          width: '800px',
          height: '800px',
          border: '8px solid #1976d2',
          backgroundColor: '#ffffff',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'flex-start',
          padding: 4,
          boxSizing: 'border-box',
          fontFamily: 'Arial, sans-serif'
        }}
      >
        {/* Header con logos */}
        <Box sx={{ 
          position: 'absolute', 
          top: 0, 
          left: 0, 
          right: 0, 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          paddingX: 4
        }}>
          {/* Logo de la empresa (solo si existe) */}
          {logoUrl ? (
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              width: 180,
              height: 100
            }}>
              <Box
                component="img"
                src={logoUrl}
                alt={`Logo de ${companyName || 'Empresa'}`}
                sx={{
                  maxHeight: '100%',
                  maxWidth: '100%',
                  width: 'auto',
                  height: 'auto'
                }}
                onError={(e) => {
                  console.error('Error cargando logo:', logoUrl);
                  // Ocultar la imagen si hay error
                  e.currentTarget.style.display = 'none';
                }}
              />
            </Box>
          ) : (
            <Box sx={{ width: 180, height: 100 }} /> // Espacio vacío para mantener el balance
          )}

          {/* VIGILANTIUM Logo */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              component="img"
              src="/logo_vigilantium_color.png"
              alt="VIGILANTIUM Logo"
              sx={{
                height: 150,
                width: 150,
                objectFit: 'contain'
              }}
            />
          </Box>
        </Box>

        {/* Título principal */}
        <Box sx={{ textAlign: 'center', mt: 8 }}>
          <Typography
            sx={{
              fontSize: '18px',
              color: '#000000',
              fontFamily: 'Arial, sans-serif',
              marginTop: 4,
              marginBottom: 2
            }}
          >
            Certifica a
          </Typography>
          
          {/* Línea decorativa */}
          <Box
            sx={{
              width: '200px',
              height: '3px',
              backgroundColor: '#1976d2',
              margin: '0 auto 20px',
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: '-6px',
                left: '50%',
                transform: 'translateX(-50%)',
                width: '12px',
                height: '12px',
                backgroundColor: '#1976d2',
                borderRadius: '50%'
              }
            }}
          />
        </Box>

        {/* Nombre del participante */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography
            sx={{
              fontSize: '32px',
              fontWeight: 'bold',
              color: '#000000',
              fontFamily: 'Arial',
              textTransform: 'uppercase',
              letterSpacing: '2px',
              marginBottom: 2
            }}
          >
            {studentName}
          </Typography>
          <Box
            sx={{
              width: '300px',
              height: '2px',
              backgroundColor: '#1976d2',
              margin: '0 auto'
            }}
          />
        </Box>

        {/* Descripción */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography
            sx={{
              fontSize: '18px',
              color: '#000000',
              fontFamily: 'Arial, sans-serif',
              marginBottom: 1
            }}
          >
            Por participar y completar el
          </Typography>
          <Typography
            sx={{
              fontSize: '32px',
              fontWeight: 'bold',
              color: '#000000',
              fontFamily: 'Arial, sans-serif'
            }}
          >
            {courseName}
          </Typography>
        </Box>


        {/* Fecha */}
        <Typography
          sx={{
            fontSize: '16px',
            color: '#000000',
            fontFamily: 'Arial, sans-serif',
            textAlign: 'center',
            marginBottom: 4
          }}
        >
          {completionDate}
        </Typography>

        {/* Firmas - todas en una sola línea, distribuidas dinámicamente */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center',
          alignItems: 'flex-end',
          mt: 6,
          px: 3,
          mb: 3,
          width: '100%',
          gap: 8
        }}>
          {/* Firma del representante de la empresa (solo si está configurada) */}
          {certificateSigner?.firstName && certificateSigner?.lastName && (
            <Box sx={{ textAlign: 'center' }}>
              {signatureUrl && (
                <Box
                  component="img"
                  src={signatureUrl}
                  alt={`Firma ${certificateSigner.firstName} ${certificateSigner.lastName}`}
                  sx={{
                    height: '55px',
                    width: 'auto',
                    marginBottom: 0.5,
                    objectFit: 'contain'
                  }}
                />
              )}
              <Typography
                sx={{
                  fontSize: '11px',
                  fontWeight: 'bold',
                  color: '#000000',
                  fontFamily: 'Arial, sans-serif',
                  marginBottom: 0.25
                }}
              >
                {certificateSigner.firstName} {certificateSigner.lastName}
              </Typography>
              {certificateSigner.position && (
                <Typography
                  sx={{
                    fontSize: '9px',
                    color: '#000000',
                    fontFamily: 'Arial, sans-serif'
                  }}
                >
                  {certificateSigner.position}
                </Typography>
              )}
            </Box>
          )}

          {/* Firma Lionel Carrillo - CTO Vigilantium (solo si showCTOSignature es true) */}
          {showCTOSignature && (
            <Box sx={{ textAlign: 'center' }}>
              <Box
                component="img"
                src="/firmalionel.png"
                alt="Firma Lionel Carrillo"
                sx={{
                  height: '55px',
                  width: 'auto',
                  marginBottom: 0.5,
                  objectFit: 'contain'
                }}
              />
              <Typography
                sx={{
                  fontSize: '11px',
                  fontWeight: 'bold',
                  color: '#000000',
                  fontFamily: 'Arial, sans-serif',
                  marginBottom: 0.25
                }}
              >
                Lionel Carrillo
              </Typography>
              <Typography
                sx={{
                  fontSize: '9px',
                  color: '#000000',
                  fontFamily: 'Arial, sans-serif'
                }}
              >
                CTO Vigilantium
              </Typography>
            </Box>
          )}

          {/* Firma Cesar Ramirez - CEO Vigilantium (siempre visible) */}
          <Box sx={{ textAlign: 'center' }}>
            <Box
              component="img"
              src="/firmacesar.png"
              alt="Firma Cesar Ramirez"
              sx={{
                height: '55px',
                width: 'auto',
                marginBottom: 0.5,
                objectFit: 'contain'
              }}
            />
            <Typography
              sx={{
                fontSize: '11px',
                fontWeight: 'bold',
                color: '#000000',
                fontFamily: 'Arial, sans-serif',
                marginBottom: 0.25
              }}
            >
              Cesar Ramirez
            </Typography>
            <Typography
              sx={{
                fontSize: '9px',
                color: '#000000',
                fontFamily: 'Arial, sans-serif'
              }}
            >
              CEO Vigilantium
            </Typography>
          </Box>
        </Box>

      </Box>

      {/* Botones de acción */}
      <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
        <Button
          variant="contained"
          startIcon={<DownloadIcon />}
          onClick={handleDownload}
          disabled={!allowViewCertificates}
          sx={{
            backgroundColor: '#1976d2',
            '&:hover': {
              backgroundColor: '#1565c0'
            },
            '&:disabled': {
              backgroundColor: '#e0e0e0',
              color: '#9e9e9e'
            }
          }}
        >
          Descargar PDF
        </Button>
        
        {!hideShareButtons && (
          <>
            <Button
              variant="contained"
              startIcon={<LinkedIn />}
              onClick={handleShareLinkedIn}
              sx={{
                backgroundColor: '#0077b5',
                '&:hover': {
                  backgroundColor: '#005885'
                }
              }}
            >
              Compartir en LinkedIn
            </Button>
            
            <Button
              variant="contained"
              startIcon={<Facebook />}
              onClick={handleShareFacebook}
              sx={{
                backgroundColor: '#1877f2',
                '&:hover': {
                  backgroundColor: '#166fe5'
                }
              }}
            >
              Compartir en Facebook
            </Button>
          </>
        )}
      </Stack>

      {/* Diálogo de instrucciones para LinkedIn */}
      <Dialog
        open={showInstructions}
        onClose={() => setShowInstructions(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Info color="primary" />
          Instrucciones para compartir en LinkedIn
        </DialogTitle>
        <DialogContent>
          <Alert severity="success" sx={{ mb: 2 }}>
            📸 ¡Se ha descargado la imagen de tu certificado!
          </Alert>
          
          <Typography variant="body1" gutterBottom>
            Para compartir tu certificado en LinkedIn:
          </Typography>
          
          <Box component="ol" sx={{ pl: 2, mt: 2 }}>
            <Typography component="li" variant="body2" sx={{ mb: 1 }}>
              <strong>IMPORTANTE:</strong> Si aparece una vista previa del enlace en LinkedIn, <strong>elimínala haciendo clic en la "X"</strong> de la vista previa. Esto es necesario para poder agregar la imagen del certificado.
            </Typography>
            <Typography component="li" variant="body2" sx={{ mb: 1 }}>
              <strong>Haz clic en "Añadir una imagen"</strong> (ícono de imagen) en la ventana de LinkedIn que se abrió
            </Typography>
            <Typography component="li" variant="body2" sx={{ mb: 1 }}>
              <strong>Sube el certificado</strong> que se descargó automáticamente en tu carpeta de descargas
            </Typography>
            <Typography component="li" variant="body2" sx={{ mb: 1 }}>
              <strong>El texto ya está prellenado</strong> - puedes editarlo si lo deseas
            </Typography>
            <Typography component="li" variant="body2" sx={{ mb: 1 }}>
              <strong>¡Publica tu logro!</strong> 🎉
            </Typography>
          </Box>
          
          <Alert severity="warning" sx={{ mt: 2 }}>
            ⚠️ <strong>Nota importante:</strong> Si no eliminas la vista previa del enlace, LinkedIn no te permitirá agregar la imagen del certificado. Asegúrate de cerrar la vista previa antes de intentar subir la imagen.
          </Alert>
          
          <Alert severity="info" sx={{ mt: 2 }}>
            💡 <strong>Tip:</strong> Puedes editar el texto antes de publicar para personalizarlo aún más.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowInstructions(false)}>
            Entendido
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CourseCertificate;
